// ── VenueCredStatus — always-on per-venue credential/connection dots ──
//
// Two tiny K / P pills with a status dot each, for the persistent chrome
// (KeyModeBanner strip + StatusBar footer). Now that a venue outage
// DEGRADES instead of blocking (Jul-9 Kalshi-maintenance lesson), the UI
// needs an always-visible answer to "which venue is actually up right
// now" — quiet enough to ignore, present enough to notice when a dot
// isn't green (Brendan, Jul-9).
//
// States:
//   Kalshi — green: connected with a live balance read;
//            amber: connected but the balance probe failed (degraded —
//                   e.g. the Thursday 3–5am ET maintenance pause);
//            red:   credential/connection error;
//            gray:  connecting / not configured.
//   Poly   — green: org pool (or personal connection) answering;
//            amber: relay answered "no poly path" (unconfigured/geoblock);
//            gray:  still resolving.

import { useAccountStore } from '@/stores/accountStore';
import { usePolyBalance } from '@/hooks/usePolyBalance';

type Tone = 'green' | 'amber' | 'red' | 'gray';

const DOT: Record<Tone, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-400',
  red: 'bg-red-500',
  gray: 'bg-gray-500',
};

function Pill({ label, tone, title }: { label: string; tone: Tone; title: string }) {
  return (
    <span
      className="text-muted-foreground inline-flex items-center gap-1 font-mono text-[10px]"
      title={title}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${DOT[tone]}`} />
      {label}
    </span>
  );
}

export function VenueCredStatus() {
  const status = useAccountStore((s) => s.status);
  const balance = useAccountStore((s) => s.balance);
  const keyMode = useAccountStore((s) => s.keyMode);
  const poly = usePolyBalance();

  let kalshiTone: Tone;
  let kalshiTitle: string;
  if (status === 'connected' && balance !== null) {
    kalshiTone = 'green';
    kalshiTitle = `Kalshi connected (${keyMode === 'org' ? 'org keys' : 'personal keys'})`;
  } else if (status === 'connected') {
    kalshiTone = 'amber';
    kalshiTitle =
      'Kalshi degraded — connected but the balance read is failing. Kalshi pauses trading Thu 3:00–5:00 AM ET for maintenance; Polymarket is unaffected.';
  } else if (status === 'error') {
    kalshiTone = 'red';
    kalshiTitle = 'Kalshi connection error — check Settings (or the Thu 3–5 AM ET maintenance window)';
  } else {
    kalshiTone = 'gray';
    kalshiTitle = status === 'connecting' ? 'Kalshi connecting…' : 'Kalshi not connected';
  }

  let polyTone: Tone;
  let polyTitle: string;
  if (poly.source !== null) {
    polyTone = 'green';
    polyTitle = `Polymarket answering (${poly.source === 'org' ? 'org keys' : 'personal keys'})`;
  } else if (poly.unavailable) {
    polyTone = 'amber';
    polyTitle =
      'Polymarket unavailable — no org poly keys on this server and no personal connection (or the relay egress is geoblocked)';
  } else {
    polyTone = 'gray';
    polyTitle = 'Polymarket status resolving…';
  }

  return (
    <span className="inline-flex items-center gap-2.5">
      <Pill label="K" tone={kalshiTone} title={kalshiTitle} />
      <Pill label="P" tone={polyTone} title={polyTitle} />
    </span>
  );
}
